// Native .webcut project file — a ZIP containing project.json plus every media
// asset (original video files; audio as 32-bit float WAV). Unlike .dawproject
// (audio-only interchange), this round-trips the full video project: tracks,
// clips, transforms, sequence settings, and embedded video/audio media.
//
// Compatibility:
// - Written as a plain stored ZIP (ZIP64 only past 4 GB), so projects under 4 GB
//   still open in earlier WebCut versions; project.json keeps the same shape and
//   new fields are optional, which older versions simply ignore.
// - project.json carries formatVersion; files from a newer WebCut open with a
//   warning. Files from any earlier version open unchanged (normalizeProject +
//   upgradeLegacyVideoAudio fill in or convert older fields).
// - Reading uses the ZIP's directory and Blob slices, so a multi-GB project is
//   never loaded into memory whole; video is played straight from the file.
// - Generated media (titles, mattes, bars & tone…) has no file — it is rebuilt
//   from its settings in project.json.
import { Project, normalizeProject, PROJECT_FORMAT_VERSION, APP_VERSION } from "../state/model";
import { Store } from "../state/store";
import { wavBytes } from "./export-wav";
import { VideoSource } from "../video/source";
import { ZipWriter, readZip, zipEntryBlob, zipEntryBytes, ZipEntry } from "./zip";
import { upgradeLegacyVideoAudio } from "./migrate";

const MEDIA = "media/";

const MIME_EXT: [string, string][] = [
  ["video/mp4", ".mp4"], ["video/webm", ".webm"], ["video/quicktime", ".mov"], ["video/x-matroska", ".mkv"],
  ["video/x-m4v", ".m4v"], ["video/ogg", ".ogv"], ["video/mp2t", ".ts"], ["video/x-msvideo", ".avi"],
  ["video/3gpp", ".3gp"], ["video/mpeg", ".mpg"], ["audio/wav", ".wav"],
];
/** File extension for a stored media blob: from its MIME type, else from the
 *  asset's original filename (files dropped from disk often have no type). */
function extFor(mime: string, name: string): string {
  const m = MIME_EXT.find(([t]) => mime.toLowerCase().startsWith(t));
  if (m) return m[1];
  if (mime.includes("matroska")) return ".mkv";
  const ext = /\.([a-z0-9]{2,5})$/i.exec(name)?.[0]?.toLowerCase();
  return ext ?? ".bin";
}
function mimeFor(filename: string): string {
  const ext = /\.[a-z0-9]+$/i.exec(filename)?.[0]?.toLowerCase() ?? "";
  return MIME_EXT.find(([, e]) => e === ext)?.[0] ?? (ext === ".mpeg" ? "video/mpeg" : "");
}

/** An independent copy of a (file-backed) blob, read in chunks. */
async function detach(blob: Blob): Promise<Blob> {
  const parts: BlobPart[] = [];
  const reader = blob.stream().getReader();
  for (;;) { const { done, value } = await reader.read(); if (done) break; parts.push(value); }
  return new Blob(parts, { type: blob.type });
}

export interface WebcutLoadReport {
  /** Media that couldn't be found or decoded (names). */
  missing: string[];
  /** The file was written by a newer WebCut; some settings may be ignored. */
  newer: boolean;
  /** The file came from an earlier format and was upgraded. */
  upgradedFrom?: number;
}

export async function exportWebcut(store: Store, onProgress?: (frac: number) => void): Promise<Blob> {
  const p = store.project;
  p.formatVersion = PROJECT_FORMAT_VERSION;
  p.savedWith = `WebCut ${APP_VERSION}`;
  const zip = new ZipWriter();
  await zip.add("project.json", JSON.stringify(p));
  const media = p.assets.filter((a) => !a.generator); // generated media is rebuilt from its spec
  let done = 0;
  for (const asset of media) {
    if (asset.kind === "video") {
      const src = store.videos.get(asset.id);
      if (src) await zip.add(MEDIA + asset.id + extFor(src.blob.type, asset.name), src.blob);
    } else {
      const buf = store.buffers.get(asset.id);
      if (buf) await zip.add(MEDIA + asset.id + ".wav", wavBytes(buf, 32)); // lossless
    }
    onProgress?.(++done / Math.max(1, media.length));
  }
  return zip.finish();
}

export async function importWebcut(data: Blob | ArrayBuffer, store: Store, ctx: AudioContext): Promise<WebcutLoadReport> {
  const file = data instanceof Blob ? data : new Blob([data]);
  let entries: Map<string, ZipEntry>;
  try { entries = await readZip(file); }
  catch (e: any) { throw new Error(`Not a readable .webcut project — ${e?.message || e}`); }
  const pj = entries.get("project.json");
  if (!pj) throw new Error("Not a .webcut project (missing project.json).");
  let project: Project;
  try { project = JSON.parse(new TextDecoder().decode(await zipEntryBytes(file, pj))); }
  catch (e: any) { throw new Error(`The project data is damaged — ${e?.message || e}`); }
  const fileVersion = typeof project.formatVersion === "number" ? project.formatVersion : 1;

  const byAsset = new Map<string, ZipEntry>();
  for (const [name, e] of entries) {
    if (!name.startsWith(MEDIA)) continue;
    const id = name.slice(MEDIA.length).replace(/\.[^.]*$/, "");
    byAsset.set(id, e);
  }

  const buffers = new Map<string, AudioBuffer>();
  const videos = new Map<string, VideoSource>();
  const missing: string[] = [];
  for (const asset of project.assets ?? []) {
    if (asset.generator) continue;
    const e = byAsset.get(asset.id);
    if (!e) { if (!asset.ownerId || !project.assets.some((a) => a.id === asset.ownerId && a.generator)) missing.push(asset.name); continue; }
    try {
      const blob = await zipEntryBlob(file, e, mimeFor(e.name));
      if (asset.kind === "video") {
        // Copy out of the project file: a slice would break as soon as the user
        // saves over this same file (the browser invalidates the old snapshot).
        videos.set(asset.id, await VideoSource.load(await detach(blob)));
        // Earliest versions: the video's sound was decoded from the video file itself.
        if (asset.hasAudio && !asset.audioAssetIds?.length) {
          try { buffers.set(asset.id, await ctx.decodeAudioData(await blob.arrayBuffer())); } catch { /* no decodable audio */ }
        }
      } else {
        buffers.set(asset.id, await ctx.decodeAudioData(await blob.arrayBuffer()));
      }
    } catch (err) {
      console.warn("[webcut] media reload failed", asset.name, err);
      missing.push(asset.name);
    }
  }

  // Swap in the loaded project only once everything above succeeded.
  for (const v of store.videos.values()) { try { v.dispose(); } catch {} }
  store.buffers.clear(); store.videos.clear();
  for (const [k, v] of buffers) store.buffers.set(k, v);
  for (const [k, v] of videos) store.videos.set(k, v);
  const normalized = normalizeProject(project);
  upgradeLegacyVideoAudio(normalized, store.buffers);
  store.project = normalized;
  store.select(normalized.tracks[0]?.id ?? null);
  store.emitChange("project");
  return { missing, newer: fileVersion > PROJECT_FORMAT_VERSION, upgradedFrom: fileVersion < PROJECT_FORMAT_VERSION ? fileVersion : undefined };
}
